<template>
  <section class="bg-white rounded-3xl border border-[#1c4848]/8 shadow-sm overflow-hidden">
    <header class="px-6 py-5 border-b border-[#1c4848]/5 flex items-center justify-between gap-4">
      <div>
        <h2 class="text-base font-bold text-[#1c4848]">Pristup i uloge</h2>
        <p class="text-xs text-[#1c4848]/50 mt-0.5">
          Klik na ulogu je dodjeljuje ili oduzima — odmah, bez čuvanja.
          Analitiku vide <span class="font-bold">super-admin</span> i <span class="font-bold">admin</span>.
        </p>
      </div>
    </header>

    <!-- Add a colleague -->
    <div class="px-6 py-4 bg-[#fafbfc] border-b border-[#1c4848]/5 relative">
      <div class="relative max-w-md">
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" class="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1c4848]/30"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
        <input
          v-model="term"
          type="text"
          placeholder="Dodaj člana — ime ili e-mail…"
          class="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#1c4848]/10 bg-white text-sm text-[#1c4848] placeholder:text-[#1c4848]/30 focus:outline-none focus:border-[#1c4848]/30"
          @input="onSearch"
        />
        <div
          v-if="store.searchResults.length"
          class="absolute left-0 right-0 top-full mt-1 bg-white rounded-xl border border-[#1c4848]/10 shadow-lg z-30 overflow-hidden"
        >
          <button
            v-for="hit in store.searchResults"
            :key="hit.id"
            type="button"
            class="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#1c4848]/5 text-left"
            @click="add(hit)"
          >
            <span class="w-7 h-7 rounded-full bg-[#1c4848]/10 text-[#1c4848] text-[10px] font-bold flex items-center justify-center shrink-0">{{ initials(hit) }}</span>
            <span class="min-w-0">
              <span class="block text-sm font-semibold text-[#1c4848] truncate">{{ hit.name }}</span>
              <span class="block text-[11px] text-[#1c4848]/50 truncate">{{ hit.email }}</span>
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Staff rows -->
    <div v-if="store.loading && !store.staff.length" class="p-6 space-y-3">
      <div v-for="i in 3" :key="i" class="h-14 rounded-2xl bg-[#1c4848]/5 animate-pulse"></div>
    </div>

    <ul v-else class="divide-y divide-[#1c4848]/5">
      <li v-for="user in store.staff" :key="user.id" class="px-6 py-4 flex flex-wrap items-center gap-x-4 gap-y-2">
        <span class="w-9 h-9 rounded-full bg-[#1c4848] text-white text-[11px] font-bold flex items-center justify-center shrink-0">{{ initials(user) }}</span>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-semibold text-[#1c4848] truncate">{{ user.name }}</p>
          <p class="text-[11px] text-[#1c4848]/50 truncate">{{ user.email }}</p>
        </div>
        <div class="flex flex-wrap items-center gap-1.5">
          <button
            v-for="role in visibleRoles"
            :key="role.id"
            type="button"
            class="px-3 py-1.5 rounded-full text-[11px] font-bold transition-all active:scale-95 border"
            :class="user.roles.includes(role.id)
              ? 'bg-[#1c4848] text-white border-[#1c4848] shadow-sm'
              : 'bg-white text-[#1c4848]/45 border-[#1c4848]/15 hover:border-[#1c4848]/40 hover:text-[#1c4848]/80'"
            :title="user.roles.includes(role.id) ? 'Klik oduzima ulogu' : 'Klik dodjeljuje ulogu'"
            @click="store.toggleRole(user, role)"
          >
            {{ role.label }}
            <span v-if="grantsAnalytics(role)" class="ml-1 opacity-70">📊</span>
          </button>
        </div>
      </li>
      <li v-if="!store.loading && !store.staff.length" class="px-6 py-8 text-center text-sm text-[#1c4848]/40">
        Još niko nema ulogu — potraži kolegu iznad i klikni mu ulogu.
      </li>
    </ul>

    <!-- Roles & permissions, tucked away until needed -->
    <div class="border-t border-[#1c4848]/5">
      <button
        type="button"
        class="w-full px-6 py-3.5 flex items-center justify-between text-left hover:bg-[#1c4848]/[0.03]"
        @click="showRoles = !showRoles"
      >
        <span class="text-xs font-bold uppercase tracking-wider text-[#1c4848]/50">Uloge i permisije</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" class="text-[#1c4848]/40 transition-transform" :class="showRoles ? 'rotate-180' : ''"><path d="m6 9 6 6 6-6"/></svg>
      </button>

      <div v-if="showRoles" class="px-6 pb-6 grid gap-4 md:grid-cols-2">
        <div
          v-for="role in store.roles"
          :key="role.id"
          class="rounded-2xl border border-[#1c4848]/8 bg-[#fafbfc] p-4"
        >
          <p class="text-sm font-bold text-[#1c4848]">{{ role.label }}</p>
          <p class="text-[11px] font-mono text-[#1c4848]/40 mb-3">{{ role.name }}</p>
          <p v-if="!store.permissions.length" class="text-[11px] text-[#1c4848]/40">Nema definisanih permisija.</p>
          <label
            v-for="permission in store.permissions"
            :key="permission.id"
            class="flex items-center gap-2.5 py-1 cursor-pointer select-none"
          >
            <input
              type="checkbox"
              class="w-4 h-4 rounded accent-[#1c4848]"
              :checked="role.permissions.includes(permission.id)"
              @change="store.togglePermission(role, permission)"
            />
            <span class="text-xs text-[#1c4848]/80">{{ permission.name }}</span>
          </label>
        </div>
      </div>
    </div>

    <p v-if="store.error" class="px-6 py-3 text-xs text-red-600 border-t border-red-100 bg-red-50">{{ store.error }}</p>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useAccessStore } from '@/store/access/index.js';

const store = useAccessStore();
const term = ref('');
const showRoles = ref(false);
let searchTimer = null;

onMounted(() => { store.fetchOverview().catch(() => {}); });

// The device auto-enrol role would only invite misclicks in a staff list.
const visibleRoles = computed(() => store.roles.filter((role) => role.name !== 'app-user'));

function grantsAnalytics(role) {
  return role.name === 'super-admin' || role.name === 'admin';
}

function initials(user) {
  const source = (user.name || user.email || '?').trim();
  return source.split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase();
}

function onSearch() {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => store.search(term.value).catch(() => {}), 250);
}

function add(user) {
  store.addToStaff(user);
  term.value = '';
}
</script>
